/**
 * Configuration pour les tests de cookies tiers
 */
const COOKIE_TEST_CONFIG = {
    // Timeout par défaut pour les tests
    defaultTimeout: 8000,
    
    // Interval entre les vérifications
    recheckInterval: 60000, // 1 minute
    
    // Domaines Keycloak selon l'environnement
    keycloakDomains: {
        production: 'keycloak.synapse42.com',
        staging: 'keycloak-staging.synapse42.com',
        development: 'localhost:8080'
    }
};

/**
 * Classe pour gérer la détection avancée des cookies tiers
 */
class AdvancedCookieDetector {
    constructor() {
        this.lastTestResult = null;
        this.lastTestTime = null;
        this.isTestInProgress = false;
        this.listeners = new Set();
    }

    /**
     * Ajoute un listener pour les changements de statut des cookies
     */
    addListener(callback) {
        this.listeners.add(callback);
    }

    /**
     * Supprime un listener
     */
    removeListener(callback) {
        this.listeners.delete(callback);
    }

    /**
     * Notifie tous les listeners d'un changement
     */
    notifyListeners(result) {
        this.listeners.forEach(callback => {
            try {
                callback(result);
            } catch (error) {
                console.error('🔧 Erreur dans le listener de cookies:', error);
            }
        });
    }

    /**
     * Obtient le domaine Keycloak selon l'environnement actuel
     */
    getKeycloakDomain() {
        const hostname = window.location.hostname;
        
        if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
            return COOKIE_TEST_CONFIG.keycloakDomains.development;
        } else if (hostname.includes('staging')) {
            return COOKIE_TEST_CONFIG.keycloakDomains.staging;
        } else {
            return COOKIE_TEST_CONFIG.keycloakDomains.production;
        }
    }

    /**
     * Test rapide des cookies tiers (utilise le cache si récent)
     */
    async quickTest() {
        const now = Date.now();
        
        // Utilise le cache si le test est récent (moins de 30 secondes pour être plus réactif)
        if (this.lastTestResult !== null && 
            this.lastTestTime && 
            (now - this.lastTestTime) < 30000) {
            // console.log('🚀 Utilisation du cache pour la détection de cookies tiers:', this.lastTestResult);
            return this.lastTestResult;
        }

        return await this.comprehensiveTest();
    }

    /**
     * Test complet des cookies tiers avec plusieurs méthodes
     */
    async comprehensiveTest() {
        if (this.isTestInProgress) {
            // console.log('⏳ Test déjà en cours, attente...');
            return this.lastTestResult;
        }

        this.isTestInProgress = true;
        
        try {
            // console.log('🔍 Démarrage du test complet de cookies tiers');
            
            // 1. Test de base des cookies
            // console.log('🍪 Test des capacités de cookies...');
            const cookieTest = this.testCookieCapabilities();
            
            if (cookieTest.cookiesEnabled && cookieTest.basicCookieSupported) {
                // console.log('✅ Test de cookies réussi - cookies supportés');
                this.updateResult(true);
                return true;
            }
            
            // 2. Si les cookies de base ne fonctionnent pas
            // console.log('❌ Les cookies semblent être bloqués ou non supportés');
            
            this.updateResult(false);
            return false;
            
        } catch (error) {
            console.error('❌ Erreur lors du test complet de cookies:', error);
            this.updateResult(false);
            return false;
        } finally {
            this.isTestInProgress = false;
        }
    }

    /**
     * Met à jour le résultat et notifie les listeners
     */
    updateResult(result) {
        const previousResult = this.lastTestResult;
        this.lastTestResult = result;
        this.lastTestTime = Date.now();
        
        // Notifie uniquement si le résultat a changé
        if (previousResult !== result) {
            // console.log(`🔄 Statut cookies tiers changé: ${previousResult} → ${result}`);
            this.notifyListeners({
                thirdPartyCookiesEnabled: result,
                timestamp: this.lastTestTime,
                method: 'comprehensive'
            });
        }
    }

    /**
     * Test simple des capacités de cookies
     */
    testCookieCapabilities() {
        try {
            // Vérifier d'abord si les cookies sont activés globalement
            if (!navigator.cookieEnabled) {
                // console.log('❌ navigator.cookieEnabled indique que les cookies sont désactivés');
                return {
                    cookiesEnabled: false,
                    basicCookieSupported: false,
                    thirdPartySupported: false
                };
            }

            // Test de cookies de base
            const testName = `test_cookie_${Date.now()}`;
            const testValue = 'test_value';
            
            // Essayer de créer un cookie standard
            document.cookie = `${testName}=${testValue}; path=/`;
            
            // Vérifier si le cookie a été créé
            const cookieExists = document.cookie.includes(`${testName}=${testValue}`);
            
            // Test plus robuste : essayer de lire le cookie créé
            let cookieReadable = false;
            if (cookieExists) {
                const cookies = document.cookie.split(';');
                cookieReadable = cookies.some(cookie => {
                    const [name, value] = cookie.trim().split('=');
                    return name === testName && value === testValue;
                });
            }
            
            // Nettoyer le cookie de test
            if (cookieExists) {
                document.cookie = `${testName}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
            }
            
            // console.log(`🍪 Test cookies - Créé: ${cookieExists}, Lisible: ${cookieReadable}`);
            
            // Pour notre cas d'usage Keycloak, si les cookies de base fonctionnent, 
            // on considère que c'est suffisant
            const isSupported = cookieExists && cookieReadable;
            
            return {
                cookiesEnabled: navigator.cookieEnabled,
                basicCookieSupported: isSupported,
                thirdPartySupported: isSupported // Simplifié pour Keycloak
            };
        } catch (error) {
            console.error('❌ Erreur lors du test de cookies:', error);
            return {
                cookiesEnabled: navigator.cookieEnabled || false,
                basicCookieSupported: false,
                thirdPartySupported: false
            };
        }
    }

    /**
     * Démarre une surveillance périodique
     */
    startPeriodicCheck() {
        // Test initial
        this.comprehensiveTest();
        
        // Tests périodiques
        this.intervalId = setInterval(() => {
            this.comprehensiveTest();
        }, COOKIE_TEST_CONFIG.recheckInterval);
        
        // console.log(`🔄 Surveillance des cookies démarrée (vérification toutes les ${COOKIE_TEST_CONFIG.recheckInterval / 1000}s)`);
    }

    /**
     * Arrête la surveillance périodique
     */
    stopPeriodicCheck() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
            // console.log('⏹️ Surveillance des cookies arrêtée');
        }
    }

    /**
     * Réinitialise le cache et force un nouveau test
     */
    async forceRefresh() {
        // console.log('🔄 Forçage d\'un nouveau test de cookies...');
        this.lastTestResult = null;
        this.lastTestTime = null;
        return await this.comprehensiveTest();
    }

    /**
     * Obtient des informations de diagnostic
     */
    getDiagnosticInfo() {
        return {
            lastResult: this.lastTestResult,
            lastTestTime: this.lastTestTime,
            timeSinceLastTest: this.lastTestTime ? Date.now() - this.lastTestTime : null,
            isTestInProgress: this.isTestInProgress,
            keycloakDomain: this.getKeycloakDomain(),
            listenerCount: this.listeners.size
        };
    }
}

// Instance singleton
const cookieDetector = new AdvancedCookieDetector();

/**
 * Hook React pour utiliser la détection avancée de cookies
 */
export const useAdvancedCookieDetection = () => {
    const [cookieStatus, setCookieStatus] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        const handleCookieStatusChange = (result) => {
            setCookieStatus(result.thirdPartyCookiesEnabled);
        };

        cookieDetector.addListener(handleCookieStatusChange);
        
        // Test initial
        setIsLoading(true);
        cookieDetector.quickTest().then(result => {
            setCookieStatus(result);
            setIsLoading(false);
        });

        return () => {
            cookieDetector.removeListener(handleCookieStatusChange);
        };
    }, []);

    const refreshStatus = async () => {
        setIsLoading(true);
        const result = await cookieDetector.forceRefresh();
        setCookieStatus(result);
        setIsLoading(false);
        return result;
    };

    return {
        thirdPartyCookiesEnabled: cookieStatus,
        isLoading,
        refresh: refreshStatus,
        diagnostics: cookieDetector.getDiagnosticInfo()
    };
};

/**
 * Fonctions utilitaires pour l'intégration
 */
export const cookieDetectionUtils = {
    // Instance du détecteur
    detector: cookieDetector,
    
    // Test rapide (avec cache)
    quickTest: () => cookieDetector.quickTest(),
    
    // Test complet (sans cache)
    fullTest: () => cookieDetector.comprehensiveTest(),
    
    // Force un nouveau test (ignore le cache)
    forceRefresh: () => cookieDetector.forceRefresh(),
    
    // Démarre la surveillance
    startMonitoring: () => cookieDetector.startPeriodicCheck(),
    
    // Arrête la surveillance
    stopMonitoring: () => cookieDetector.stopPeriodicCheck(),
    
    // Test immédiat sans cache
    immediateTest: () => {
        cookieDetector.lastTestResult = null;
        cookieDetector.lastTestTime = null;
        return cookieDetector.comprehensiveTest();
    },
    
    // Configuration
    config: COOKIE_TEST_CONFIG
};

export default AdvancedCookieDetector;